import { useTranslation } from 'react-i18next';
import {
  assign,
  ContextualMenu,
  FontWeights,
  getTheme,
  IBasePickerSuggestionsProps,
  IconButton,
  IDragOptions,
  IIconProps,
  IStackItemStyles,
  IStackStyles,
  IStackTokens,
  Label,
  mergeStyleSets,
  Modal,
  PrimaryButton,
  Stack,
  TextField,
} from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import { useBoolean, useId } from '@uifabric/react-hooks';
import _ from 'lodash';
import { uPrinceTheme } from '../../../../../theme';
import { Resource } from '../../../../types/progressStatement';
import i18n from '../../../../../i18n';
import GroupDropdown from '../../../../shared/groupDropdown/groupDropdown';

const theme = getTheme();

const iconButtonStyles = {
  root: {
    color: uPrinceTheme.palette.themePrimary,
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};

const classNames = mergeStyleSets({
  wrapper: {
    //height: '100vh',
    position: 'relative',
    maxHeight: 'inherit',
  },
  pane: {
    maxWidth: 400,
  },
  textContent: {
    padding: '15px 10px',
  },
  actionIcon: {
    padding: 5,
    cursor: 'pointer',
  },
  labelRightWrapper: {
    textAlign: 'end',
    selectors: {
      '@media(max-width: 1100px)': {
        textAlign: 'start',
      },
    },
  },
  actionButton: {
    color: uPrinceTheme.palette.themePrimary,
    cursor: 'pointer',
  },
  labelRightIcon: {
    display: 'inline-block',
    verticalAlign: 'bottom',
    paddingBottom: 2,
    paddingRight: 5,
  },
  fullWidth: {
    width: '100%',
  },
});

const stackStyles: IStackStyles = {
  root: {
    padding: 0,
  },
};
const stackItemStyles: IStackItemStyles = {
  root: {
    display: 'flex',
    height: 50,
    paddingBottom: 10,
    flexGrow: 1,
    width: 0,
  },
};

const bankItemStyles: IStackItemStyles = {
  root: {
    display: 'flex',
    height: 50,
    paddingBottom: 0,
    flexGrow: 1,
    width: 0,
  },
};

const dragOptions: IDragOptions = {
  moveMenuItemText: 'Move',
  closeMenuItemText: 'Close',
  menu: ContextualMenu,
};
const cancelIcon: IIconProps = { iconName: 'Cancel' };
const addWhiteIconButtonStyles = {
  root: {
    color: uPrinceTheme.palette.white,
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};
const addIcon: IIconProps = { iconName: 'Add', styles: iconButtonStyles };
const addIconWhite: IIconProps = {
  iconName: 'Save',
  styles: addWhiteIconButtonStyles,
};
const contentStyles = mergeStyleSets({
  container: {
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'stretch',
  },
  actionButtonLabel: {
    color: uPrinceTheme.palette.themePrimary,
  },
  header: [
    // eslint-disable-next-line deprecation/deprecation
    theme.fonts.xLargePlus,
    {
      flex: '1 1 auto',
      display: 'flex',
      alignItems: 'center',
      fontWeight: FontWeights.semibold,
      padding: '2px 2px 2px 10px',
      backgroundColor: uPrinceTheme.palette.themePrimary,
      fontSize: 18,
      color: 'white',
    },
  ],
  footer: [
    // eslint-disable-next-line deprecation/deprecation
    theme.fonts.xLargePlus,
    {
      flex: '1 1 auto',
      alignItems: 'center',
      fontWeight: FontWeights.semibold,
      padding: '0px 24px 14px 24px',
      textAlign: 'end',
    },
  ],
  body: {
    flex: '4 4 auto',
    padding: '0 24px 0px 24px',
    paddingTop: 20,
    overflowY: 'hidden',
    minWidth: '40vw',
    //minHeight: '32vh',
    minHeight: screen.width > 1280 ? '32vh' : '50vh',
    selectors: {
      p: { margin: '14px 0' },
      'p:first-child': { marginTop: 0 },
      'p:last-child': { marginBottom: 0 },
    },
  },
  subHeader: {
    flex: '1 1 auto',
    display: 'none',
    alignItems: 'center',
    fontWeight: FontWeights.semibold,
    padding: '2px 2px 2px 10px',
    fontSize: 14,
    color: uPrinceTheme.palette.themePrimary,
    backgroundColor: '#FFF7F4',
    height: 40,
    marginLeft: 'auto',
    marginRight: '2px',
  },
});
const toggleStyles = { root: { marginBottom: '20px' } };
const cancelIconButtonStyles = {
  root: {
    marginLeft: 'auto',
    marginTop: '4px',
    marginRight: '2px',
    color: 'white',
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};

const stackTokens: IStackTokens = {
  childrenGap: 10,
  padding: 10,
};

const dropdownStyles = { dropdown: { width: 300, marginTop: 20 } };

const tagSearchAdditionalProps: IBasePickerSuggestionsProps = {
  suggestionsContainerAriaLabel: i18n.t('suggestedOrganisation'),
  searchForMoreText: i18n.t('loadallResults'),
  resultsMaximumNumber: 10,
  searchingText: i18n.t('searching') + '...',
};

const tagSuggestionProps: IBasePickerSuggestionsProps = {
  suggestionsHeaderText: i18n.t('suggestedOrganisation'),
  mostRecentlyUsedHeaderText: i18n.t('suggestedOrganisation'),
  noResultsFoundText: i18n.t('noresultsfound'),
  loadingText: i18n.t('loading'),
  showRemoveButtons: true,
  suggestionsAvailableAlertText: i18n.t('peoplePickerSuggestionsavailable'),
  suggestionsContainerAriaLabel: i18n.t('suggestedOrganisation'),
};

const tagSearchSuggestionProps: IBasePickerSuggestionsProps = assign(
  tagSearchAdditionalProps,
  tagSuggestionProps,
);

class MaterialValidationMessages {
  cpcValidationMsg: string = '';
  quantityValidationMsg: string = '';
}

class PickerPerson {
  key: string | null = '';
  text: string | null = '';
}

const ResourceModal = (props: {
  isOpen: boolean;
  openResourceModal: () => void;
  handleResourceSave: (quantity: any) => void;
  handleCpcChange: (selectedCpc: any) => void;
  title: string;
  cpcOptions: any;
  cpcData: any;
  clearCpcData: () => void;
  isEdit: boolean;
  resource: Resource;
  resourceTypeId: string | null;
  type: 'Planned' | 'Extra';
}) => {
  const { t } = useTranslation();
  const [isDraggable, { toggle: toggleIsDraggable }] = useBoolean(true);
  const titleId = useId('title');
  const theme = getTheme();

  const [coefficient, setCoefficient]: any = useState('');
  const [fixedPrice, setFixedPrice]: any = useState('');
  const [cpc, setCpc]: any = useState(null);
  const [nameErrorMsg, setNameErrorMsg]: any = useState(null);
  const [coefficientErrorMsg, setCoefficientErrorMsg]: any = useState(null);
  const [fixedPriceErrorMsg, setFixedPriceErrorMsg]: any = useState(null);


  const onChangeInput = (field: string) => (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string,
  ) => {
    if (field === 'coefficient') {
      setCoefficient(newValue);
      setCoefficientErrorMsg(null);
      setFixedPriceErrorMsg(null);
    }
    if (field === 'fixedPrice') {
      setFixedPrice(newValue);
      setCoefficientErrorMsg(null);
      setFixedPriceErrorMsg(null);
    }

  };

  const onSave = () => {
    if (validate()) {
      if (props.isEdit) {
        props.resource.cpcId = (cpc && cpc.id === null) ? props.cpcData.id : props.resource.cpcId;
        props.resource.resourceTypeId = props.resourceTypeId;
        props.resource.coefficient = coefficient;
        props.resource.fixedPrice = fixedPrice;
        props.resource.title = (cpc) ? cpc.label : props.resource.title;
        props.resource.environment = (cpc) ? cpc.environment : props.resource.environment;
        props.handleResourceSave(props.resource);
        onModalClose();
        props.clearCpcData();
      } else {
        props.resource.cpcId = props.cpcData.id;
        props.resource.coefficient = coefficient;
        props.resource.fixedPrice = fixedPrice;
        props.resource.title = props.cpcData.resourceTitle;
        props.resource.resourceTypeId = props.resourceTypeId;
        props.resource.environment = cpc.environment;
        props.handleResourceSave(props.resource);
        onModalClose();
        props.clearCpcData();
      }

    } else {

    }
  };


  const validate = (): boolean => {
    let isValid = true;
    if (_.isNil(cpc) || _.isEmpty(cpc)) {
      setNameErrorMsg(t('titleRequired'));
      isValid = false;
    } else {
      setNameErrorMsg('');
    }
    if (_.isNil(coefficient) || _.isEmpty(coefficient) && (_.isNil(fixedPrice) || _.isEmpty(fixedPrice))) {
      if (!coefficient && !fixedPrice) {
        setCoefficientErrorMsg(t('Coefficient or fixed Price required'));
        setFixedPriceErrorMsg(t('Coefficient or fixed Price required'));
        isValid = false;
      }

    } else {
      setCoefficientErrorMsg('');
      setFixedPriceErrorMsg('');
    }
    // if (_.isNil(fixedPrice) || _.isEmpty(fixedPrice)) {
    //   if(!fixedPrice){
    //     setFixedPriceErrorMsg(t('Fixed Price required'));
    //     isValid = false;
    //   }
    //
    // } else {
    //   setFixedPriceErrorMsg("");
    // }
    return isValid;
  };

  const resetValidation = () => {
    setNameErrorMsg(null);
    setCoefficientErrorMsg(null);
    setFixedPriceErrorMsg(null);
  };

  const validateResourceName = (resource: Resource) => {
    let isValidName = false;
    if (resource && resource.cpcId) {
      isValidName = true;
      //setRiskValidationState('cpcValidationMsg', '');
    } else {
      isValidName = false;
      // setRiskValidationState('cpcValidationMsg', t('nameRequired'));
    }
    return isValidName;
  };


  useEffect(() => {
    if (cpc) {
      props.handleCpcChange(cpc);
    }
  }, [cpc]);

  useEffect(() => {
    if (props.resource) {
      setDataToEdit();
    }
  }, [props.resource]);


  const setDataToEdit = () => {
    if (props.resource.id) {
      setCoefficient((props.resource.coefficient) ? props.resource.coefficient : null);
      setFixedPrice((props.resource.fixedPrice) ? props.resource.fixedPrice : null);
      setCpc({
        id: props.resource.cpcId,
        value: props.resource.cpcId,
        label: props.resource.title,
      });
    } else {
      setCoefficient('');
      setFixedPrice('');
      setCpc(null);
    }

  };

  const onModalClose = () => {
    if (!props.resource.id) {
      setCoefficient('');
      setFixedPrice('');
      setCpc(null);
    }
    props.openResourceModal();
    resetValidation();
  };

  return (

    <div>
      <Modal
        titleAriaId={titleId}
        isOpen={props.isOpen}
        onDismiss={() => {
          onModalClose();
        }}
        scrollableContentClassName="body"
        isBlocking={false}
        dragOptions={undefined}
      >
        <div className={contentStyles.header}>
          <span id={titleId}>{props.title}</span>
          <IconButton
            styles={cancelIconButtonStyles}
            iconProps={cancelIcon}
            ariaLabel="Close popup modal"
            onClick={() => {
              onModalClose();
            }}
          />
        </div>
        <div className={contentStyles.subHeader}>

        </div>
        <div className={contentStyles.body}>
          <div className="proj-detail-content inner">

            <div className="ms-Grid-row">
              <Stack horizontal wrap styles={stackStyles} tokens={stackTokens}>
                <Stack.Item grow={6} styles={stackItemStyles}>
                  <div className={`${classNames.fullWidth}`}>
                    <Label required={true}>{t('resourceTitle')} </Label>
                    <GroupDropdown
                      autoFocus={true}
                      onChange={(key: string, text: string, environment: string) => {
                        if (key) {
                          let resource = { id: null, value: key, label: text, environment: environment };
                          setCpc(resource);
                          setNameErrorMsg(null);
                        } else {
                          props.clearCpcData();
                          setCpc(null);
                        }
                        // alert(key);
                      }}
                      selectedOption={(cpc) ? {
                        value: cpc.value,
                        label: cpc.label,
                      } : null}
                      promiseOptions={props.cpcOptions}
                      validationMessage={nameErrorMsg}
                      required={true}
                      reference={''}
                    />
                  </div>
                </Stack.Item>
                <Stack.Item grow={6} styles={stackItemStyles}>
                  <div className={`${classNames.fullWidth}`}>

                  </div>
                </Stack.Item>
              </Stack>
            </div>
            <div className="ms-Grid-row" style={{ marginTop: 20 }}>
              <Stack horizontal wrap styles={stackStyles} tokens={stackTokens}>
                <Stack.Item grow={6} styles={stackItemStyles}>
                  <div className={`${classNames.fullWidth}`}>
                    <TextField
                      label={t('fixedPrice')}
                      value={fixedPrice}
                      errorMessage={fixedPriceErrorMsg}
                      type={'number'}
                      required={!coefficient}
                      onChange={onChangeInput('fixedPrice')}
                      autoComplete={'off'}
                    />
                  </div>
                </Stack.Item>
                <Stack.Item grow={6} styles={stackItemStyles}>
                  <div className={`${classNames.fullWidth}`}>
                    <TextField
                      label={t('coefficient')}
                      value={coefficient}
                      errorMessage={coefficientErrorMsg}
                      type={'number'}
                      required={!fixedPrice}
                      onChange={onChangeInput('coefficient')}
                      autoComplete={'off'}
                    />
                  </div>
                </Stack.Item>
              </Stack>
            </div>
          </div>
        </div>
        <div className={contentStyles.footer}>
          <PrimaryButton
            iconProps={addIconWhite}
            text={t('save')}
            style={{ marginTop: 20, marginBottom: 30 }}
            onClick={() => {
              onSave();
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ResourceModal;
